export type AddressComponents = {
  address_st_number?: string;
  address_st_name?: string;
  address_unit?: string;
  address_city?: string;
  address_state?: string;
  address_zip?: string;
};

export interface AddressParts {
  address_st_number?: string;
  address_st_name?: string;
  address_unit?: string;
  address_city?: string;
  address_state?: string;
  address_zip?: string;
}

export const formatAddressComponents = (
  addressComponents: any[]
): AddressComponents => {
  const address: AddressComponents = {};

  addressComponents.forEach((component) => {
    const types = component.types;

    if (types.includes('street_number')) {
      address.address_st_number = component.long_name;
    }

    if (types.includes('route')) {
      address.address_st_name = component.long_name;
    }

    if (types.includes('subpremise')) {
      address.address_unit = component.long_name;
    }

    if (types.includes('locality')) {
      address.address_city = component.long_name;
    }

    if (types.includes('administrative_area_level_1')) {
      address.address_state = component.short_name;
    }

    if (types.includes('postal_code')) {
      address.address_zip = component.long_name;
    }
  });

  return address;
};

export const getFormattedAddressObject = (
  addressComponents: any[]
): AddressComponents => {
  const address: AddressComponents = {
    address_city: undefined,
    address_st_name: undefined,
    address_st_number: undefined,
    address_state: undefined,
    address_zip: undefined,
    address_unit: undefined,
  };

  addressComponents.forEach((component) => {
    const types = component.types;

    if (types.includes('street_number')) {
      address.address_st_number = component.longText;
    }

    if (types.includes('route')) {
      address.address_st_name = component.longText;
    }

    if (types.includes('subpremise')) {
      address.address_unit = component.longText;
    }

    if (types.includes('locality')) {
      address.address_city = component.longText;
    }

    if (types.includes('administrative_area_level_1')) {
      address.address_state = component.shortText;
    }

    if (types.includes('postal_code')) {
      address.address_zip = component.longText;
    }
  });

  return address;
};

/**
 * Whether these parts amount to an address that can stand as one line.
 *
 * Lives beside `getFullAddress` because it is the question that composer cannot answer for
 * itself: it filters the empties, so a half address composes into something that reads as clean
 * rather than broken — "RD 2, GENEVA, NY, 14456" is a real registry address with no street
 * number, and nothing downstream would notice. That one line is also what the signup steps treat
 * as proof of a Google Places selection, which is the only check between a typed string and a
 * real one, so composing it from a partial address is how an address Places never returned
 * reaches KYB.
 *
 * The unit is deliberately not required — most addresses have none. Everything else is.
 */
export const REQUIRED_ADDRESS_PART_FIELDS = [
  'address_st_number',
  'address_st_name',
  'address_city',
  'address_state',
  'address_zip',
] as const;

export type RequiredAddressPart = (typeof REQUIRED_ADDRESS_PART_FIELDS)[number];

/** A part is given when it is a string with something besides whitespace in it. */
export const isFilledAddressPart = (value: unknown): boolean =>
  typeof value === 'string' && value.trim().length > 0;

/**
 * The required parts these lack, in the order the form asks for them.
 *
 * `isCompleteAddress` is built on this, so anything that names what is missing — a message, a
 * field rule — cannot disagree with the check about what counts as missing.
 */
export const getMissingAddressParts = (
  parts: Partial<Record<keyof AddressParts, unknown>> | null | undefined
): RequiredAddressPart[] =>
  REQUIRED_ADDRESS_PART_FIELDS.filter(
    (field) => !isFilledAddressPart(parts?.[field])
  );

export const isCompleteAddress = (
  parts: Partial<Record<keyof AddressParts, unknown>> | null | undefined
): boolean => getMissingAddressParts(parts).length === 0;

/**
 * A unit that is only whitespace or the "-" placeholder some records carry is no unit at all.
 * Returns the trimmed unit, or '' when there is none.
 */
export const cleanAddressUnit = (unit: string | null | undefined): string => {
  const trimmed = unit?.trim() ?? '';
  return trimmed === '-' ? '' : trimmed;
};

/** "1125 S Industrial Rd #4" — number, street and unit (when there is one) on one line. */
export const getStreetLine = (parts: AddressParts): string => {
  const unit = cleanAddressUnit(parts?.address_unit);
  return [
    parts?.address_st_number,
    parts?.address_st_name,
    unit ? `#${unit}` : '',
  ]
    .filter(Boolean)
    .join(' ');
};

export const getFullAddress = (parts: AddressParts): string => {
  if (!parts) return '';

  const street = getStreetLine(parts);

  const cityState = [parts.address_city, parts.address_state]
    .filter(Boolean)
    .join(', ');

  const zip = parts.address_zip ?? '';

  // Join all non-empty parts with commas
  return [street, cityState, zip].filter(Boolean).join(', ');
};
